import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { useCallback, useEffect, useState } from "react";
import { ILead } from "../../../../types/lead.type";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import InputFormik from "../../../../common/components/form/InputFormik";
import { maskCPF, maskPhone } from "../../../../common/helpers/mask.helpers";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { IIndicators } from "../../../../types/Indicators.type";
import { updateLeadProps } from "../../../../common/services/leads.api";

// Esquema de validação com Yup
const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Nome é obrigatório")
    .test(
      "full-name",
      "Deve incluir pelo menos um nome e um sobrenome",
      (value) => {
        if (!value) return false;
        const parts = value.split(" ");
        return parts.length >= 2;
      }
    ),
  whatsapp: Yup.string().required("Número do WhatsApp obrigatório"),
});

type FormPropsCreateLead = {
  onSubmit: (
    values: ILead
  ) => Promise<{ ok: boolean; data: ILead; errors?: unknown[] }>;
  data: ILead;
  dataAmbassador: IIndicators;
  formRequestErrors?: { message: string; path: string[] }[];
};

function FormEditLeadByAmbassador(props: FormPropsCreateLead) {
  const { notifySuccess } = useToastContext();
  const [theInitialData, setNewInitialData] = useState<ILead>(props.data);

  const updateCpfDoIndicador = useCallback(
    async (leadId) => {
      if (!leadId) return;

      const dataToSave = {
        cpfDoIndicador: props.dataAmbassador.cpf || undefined,
        references: [
          { name: "-", content: "-", verified: false },
          { name: "-", content: "-", verified: false },
          { name: "-", content: "-", verified: false },
        ],
      };

      await updateLeadProps(leadId, dataToSave);
    },
    [props.dataAmbassador.cpf]
  );

  useEffect(() => {
    if (props.data._id) {
      updateCpfDoIndicador(props.data._id);
    }
  }, [props.data._id, updateCpfDoIndicador]);

  useEffect(() => {
    setNewInitialData(props.data);
  }, [props.data]);

  const formik = useFormik({
    initialValues: theInitialData,
    validationSchema: validationSchema,
    onSubmit: async (data: ILead) => {
      const lead = {
        ...data,
        companyId: props.dataAmbassador.companyId,
        ambassadorName: props.dataAmbassador.name,
        cpfDoIndicador: props.dataAmbassador.cpf,
        cpfDoIndicadorOptions: {
          indicated: true,
          notIndicated: false,
        },
        leadOrigin: "Embaixador",
      };
      const result = await props.onSubmit(lead);
      if (result.ok) {
        formik.setValues(result.data);
        setNewInitialData(result.data);
        notifySuccess("Atualizado com sucesso.");
      }
      return result;
    },
  });

  const [, setCurrentDate] = useState(new Date().toISOString().split("T")[0]);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date().toISOString().split("T")[0]);
    }, 60000);
    return () => clearInterval(interval); // Limpa o intervalo quando o componente é desmontado
  }, []);

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold mb-4">Informações Pessoais</h2>

          <div className="grid grid-cols-3 gap-2 mb-4">
            {/* Primeira linha */}
            <div className="grid grid-cols-2 text-sm lg:text-lg col-span-4 gap-2 sm:grid-cols-2 xl:grid-cols-2">
              <InputFormik
                label="Nome completo:"
                name="name"
                className=""
                required={true}
              />
              <InputFormik
                label="WhatsApp:"
                name="whatsapp"
                type="whatsapp"
                required={true}
                mask={maskPhone}
              />

              <InputFormik
                label="CPF:"
                name="cpf"
                placeholder="111.222.333-44"
                required={false}
                mask={maskCPF}
              />
              <InputFormik
                label="E-mail:"
                name="email"
                type="email"
                required={false}
              />
            </div>
          </div>
        </section>

        <div className="w-full flex justify-end">
          <ButtonWithLoader
            isLoading={formik.isSubmitting}
            disabled={!formik.isValid}
            onClick={formik.handleSubmit}
          >
            Salvar
          </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  );
}

export default FormEditLeadByAmbassador;
